import { commandAnchorSchema } from '@tunasong/schemas'
import { z } from 'zod'
import type { Tool, ToolCallContext, ToolInvalidArgumentsDetails } from '../types.js'

export interface ProposedEditSummary {
  proposalId: string
}

/**
 * Strict wire format for slate-ops patches. When `anchor` is present, the
 * consumer rewrites each op's `path` by prepending the resolved anchor path
 * (+ optional `subPath`). When absent, ops apply at the document root.
 *
 * Agents authoring slate-ops should ALWAYS anchor — they don't have access
 * to absolute Slate paths. On the persisting (default) path, an entirely
 * omitted `anchor` remains a backward-compat escape hatch for non-agent
 * slate-ops emitters and falls through to the document root, even when the
 * turn carries focus context. In INLINE mode only, an omitted (or `id`-less)
 * `anchor` instead defaults to `ctx.focus.id` — the interactive turn is
 * always block-scoped, so there is no root-escape-hatch use case there.
 */
const slateAnchorSchema = z.object({
  id: z.string().min(1).describe('Stable block.id to anchor the ops at.'),
  subPath: z
    .array(z.number().int().nonnegative())
    .describe('Optional Slate sub-path inside the anchor block, prepended to every op path before the relative segment.')
    .optional(),
})

const slatePatchSchema = z.object({
  kind: z.literal('slate-ops'),
  anchor: slateAnchorSchema
    .describe('When set, every op.path is interpreted RELATIVE to this block. Omit to apply ops at document root.')
    .optional(),
  ops: z.array(z.record(z.string(), z.unknown())).min(1).describe('Slate Operation objects to apply in order.'),
})

/**
 * Tool-input variant: `anchor.id` is optional so the agent can defer to
 * `ctx.focus.id`. Handler narrows to the strict wire shape (anchor.id
 * filled or anchor absent) before forwarding.
 */
const slateAnchorInputSchema = z.object({
  id: z
    .string()
    .min(1)
    .describe('Stable block.id. Defaults to ctx.focus.id when omitted and the turn has anchor context.')
    .optional(),
  subPath: slateAnchorSchema.shape.subPath,
})

const slatePatchInputSchema = z.object({
  kind: z.literal('slate-ops'),
  anchor: slateAnchorInputSchema
    .describe('When set, every op.path is interpreted RELATIVE to this block. Omit to apply at document root.')
    .optional(),
  ops: slatePatchSchema.shape.ops,
})

const entityMetadataPatchSchema = z.object({
  kind: z.literal('entity-metadata'),
  fields: z
    .object({
      name: z.string().optional(),
      description: z.string().optional(),
      tags: z.array(z.string()).optional(),
    })
    .refine(f => Object.keys(f).length > 0, { message: 'fields must not be empty' })
    .describe('Partial entity fields to update.'),
})

/**
 * Strict wire format for editor-command patches. `anchor` uses the canonical
 * `commandAnchorSchema` (id required) so downstream consumers of `AgentPatch`
 * (e.g. proposal-apply) can rely on `anchor.id: string`.
 */
const editorCommandPatchSchema = z.object({
  kind: z.literal('editor-command'),
  commandId: z.string().min(1).describe('Registered editor command id, e.g., "chord.insert".'),
  args: z.record(z.string(), z.unknown()).describe('Command-specific arguments.').optional(),
  anchor: commandAnchorSchema.describe('Where in the entity tree the command should act.'),
})

/**
 * Loose tool-input variant of `commandAnchorSchema`. The wire format stays
 * strict (id required) — this schema only widens the *tool surface* so the
 * agent can omit `anchor.id` when the turn carries focus context. The handler
 * fills `id` from `ctx.focus.id` before forwarding, or returns a structured
 * invalid-args result. Do not export — `commandAnchorSchema` remains the
 * source of truth for downstream consumers.
 */
const looseAnchorSchema = z.object({
  id: z
    .string()
    .min(1)
    .describe('Stable block.id of the target. Defaults to ctx.focus.id when omitted and the turn has anchor context.')
    .optional(),
  position: commandAnchorSchema.shape.position,
  subPath: commandAnchorSchema.shape.subPath,
})

/**
 * Tool-input-only variant of `editorCommandPatchSchema`. Same shape, but
 * `anchor.id` is optional so the agent can defer to `ctx.focus.id`. The
 * handler narrows back to the strict wire format before forwarding to
 * `deps.createProposal`.
 */
const editorCommandPatchInputSchema = z.object({
  kind: z.literal('editor-command'),
  commandId: editorCommandPatchSchema.shape.commandId,
  args: editorCommandPatchSchema.shape.args,
  anchor: looseAnchorSchema.describe('Where in the entity tree the command should act.'),
})

export const agentPatchSchema = z.discriminatedUnion('kind', [
  slatePatchSchema,
  entityMetadataPatchSchema,
  editorCommandPatchSchema,
])

export type AgentPatch = z.infer<typeof agentPatchSchema>
export type SlateOpsPatch = z.infer<typeof slatePatchSchema>
export type EntityMetadataPatch = z.infer<typeof entityMetadataPatchSchema>
export type EditorCommandPatch = z.infer<typeof editorCommandPatchSchema>

export interface ProposeEditDeps {
  /** Create a pending diff against the given entity. Handler returns a proposalId the user can accept/reject. */
  createProposal: (input: {
    agentId: string
    entityId: string
    userId: string
    summary: string
    patch: AgentPatch
  }) => Promise<ProposedEditSummary>
  /**
   * Optional structural validation run BEFORE createProposal for slate-ops
   * patches. Returns `{ ok: false, reason }` to reject the patch (the handler
   * converts it to an `invalid_arguments` result so the agent retries — ideally
   * as an editor-command). Injected because only the server can load the live
   * document tree. Absent (e.g. unit tests) → fail-open, no validation.
   */
  validatePatch?: (input: {
    entityId: string
    patch: AgentPatch
  }) => Promise<{ ok: true } | { ok: false; reason: string }>
}

/**
 * Tool-input union. Same as `agentPatchSchema` except the editor-command
 * branch uses the loose anchor (id optional). Not exported — wire consumers
 * should depend on `agentPatchSchema` / `AgentPatch`.
 */
const proposeEditPatchInputSchema = z.discriminatedUnion('kind', [
  slatePatchInputSchema,
  entityMetadataPatchSchema,
  editorCommandPatchInputSchema,
])

const proposeEditInputSchema = z.object({
  entityId: z.string().min(1).describe('The id of the entity to edit.'),
  summary: z.string().min(1).describe('One-sentence summary of what this edit changes.'),
  patch: proposeEditPatchInputSchema.describe(
    'Typed patch describing the edit. PREFER kind: "editor-command" for structural edits that have a ' +
      'registered command (insert/replace/delete a chord, insert/delete a line, add/remove a section, ' +
      'set a section scale) — give commandId, ' +
      'an anchor block id, and a position. Use kind: "slate-ops" ONLY for free-text edits inside an existing ' +
      'line, with a single relative op path. Use kind: "entity-metadata" for name/description/tags.'
  ),
})

/**
 * Result of resolving + validating a propose_edit call: either the
 * fully-resolved wire-format patch (anchor.id defaulted from ctx.focus.id
 * where applicable) ready to persist or return inline, or a structured
 * invalid_arguments failure to hand straight back to the caller.
 */
type ResolvedPatch =
  | { ok: true; summary: string; patch: AgentPatch }
  | { ok: false; error: 'invalid_arguments'; invalidArguments: ToolInvalidArgumentsDetails }

/**
 * Non-persisting counterpart to `ProposedEditSummary`. Returned by
 * `createProposeEditTool(deps, { inline: true })` instead of calling
 * `deps.createProposal` — the interactive-turn caller renders `patch`
 * directly (e.g. as an inline ghost diff) without a persisted proposal.
 *
 * Narrowed to `SlateOpsPatch | EditorCommandPatch` — the inline,
 * in-document ghost-diff flow only ever edits document content.
 * `entity-metadata` patches are rejected before this type is constructed
 * (see the `opts.inline` branch in the handler below); they remain valid
 * on the persisting (`ProposedEditSummary`) path.
 */
export interface InlineEditResult {
  kind: 'inline-edit'
  summary: string
  patch: SlateOpsPatch | EditorCommandPatch
}

/**
 * Validate + default-anchor + resolve the tool-input patch into the strict
 * wire format shared by both the persisting and inline modes of
 * `createProposeEditTool`.
 */
const resolvePatch = async (
  args: z.infer<typeof proposeEditInputSchema>,
  ctx: ToolCallContext,
  deps: ProposeEditDeps,
  opts: { inline?: boolean } = {}
): Promise<ResolvedPatch> => {
  const inputPatch = args.patch
  // Structural pre-validation for slate-ops (the path-correctness failure
  // class). Runs only when the server injects validatePatch and the patch is
  // slate-ops; editor-command/entity-metadata go straight through.
  const slateAnchorId = inputPatch.kind === 'slate-ops' ? (inputPatch.anchor?.id ?? ctx.focus?.id) : undefined
  // Skip validation when the patch HAS an anchor but no resolvable id — the
  // existing missing-anchor.id guard below returns the precise error and a
  // tailored recovery; don't preempt it with a generic "anchor not found".
  const anchorResolvable = inputPatch.kind === 'slate-ops' && (!inputPatch.anchor || Boolean(slateAnchorId))
  if (deps.validatePatch && inputPatch.kind === 'slate-ops' && anchorResolvable) {
    const anchorId = slateAnchorId
    const patchForValidation = {
      ...inputPatch,
      anchor: inputPatch.anchor ? { ...inputPatch.anchor, id: anchorId } : undefined,
    } as AgentPatch
    const verdict = await deps.validatePatch({ entityId: args.entityId, patch: patchForValidation })
    if (!verdict.ok) {
      return {
        ok: false,
        error: 'invalid_arguments',
        invalidArguments: {
          tool: 'propose_edit',
          issues: [{ path: 'patch.ops', message: verdict.reason }],
          recovery:
            `Your slate-ops patch does not apply to the current document (${verdict.reason}). ` +
            `Prefer a kind: 'editor-command' patch for structural edits — e.g. 'chord.insert', ` +
            `'line.insert', 'section.add' — naming the commandId, an anchor block id from the ` +
            `entity outline, and a position. Only use slate-ops for free-text edits inside an ` +
            `existing line, with a single relative op path. Do not surface this to the user as chat.`,
        },
      }
    }
  }
  // Persisting (default) path: only an anchor OBJECT with a missing id
  // defaults from focus — an entirely omitted anchor is the document-root
  // escape hatch and must fall through untouched (see module doc comment).
  // Inline path: the interactive turn is always block-scoped, so an
  // omitted anchor ALSO defaults from focus, same as an id-less anchor.
  const slateAnchorNeedsDefault =
    inputPatch.kind === 'slate-ops' &&
    (opts.inline ? !inputPatch.anchor?.id : Boolean(inputPatch.anchor && !inputPatch.anchor.id))
  if (slateAnchorNeedsDefault) {
    // Defaults anchor.id from ctx.focus.id whenever focus context is present —
    // whether the agent supplied an anchor object with a missing id, or
    // (inline mode only) omitted the anchor field entirely.
    if (ctx.focus?.id) {
      const patch: AgentPatch = {
        ...inputPatch,
        anchor: { id: ctx.focus.id, ...(inputPatch.anchor?.subPath ? { subPath: inputPatch.anchor.subPath } : {}) },
      }
      return { ok: true, summary: args.summary, patch }
    }
    if (inputPatch.anchor) {
      return {
        ok: false,
        error: 'invalid_arguments',
        invalidArguments: {
          tool: 'propose_edit',
          issues: [
            {
              path: 'patch.anchor.id',
              message:
                'slate-ops anchor.id is required when the turn has no focus. Either pass anchor.id explicitly, omit the anchor entirely to apply at document root, or invoke from a block-anchored ask.',
            },
          ],
          recovery:
            `Your last call to propose_edit used an anchored slate-ops patch but did not supply anchor.id, ` +
            `and the turn has no focus context. Either re-call with an explicit anchor.id pulled from the ` +
            `entity outline, or drop the anchor field entirely to apply ops at document root. ` +
            `Do not surface this validation error to the user as a chat message.`,
        },
      }
    }
    // No anchor field at all, and no focus context — unanchored escape hatch:
    // fall through to apply ops at document root.
  }
  if (inputPatch.kind === 'editor-command' && !inputPatch.anchor.id) {
    if (!ctx.focus?.id) {
      return {
        ok: false,
        error: 'invalid_arguments',
        invalidArguments: {
          tool: 'propose_edit',
          issues: [
            {
              path: 'patch.anchor.id',
              message:
                'anchor.id is required when the turn has no focus. Pass patch.anchor.id explicitly or invoke from a block-anchored ask.',
            },
          ],
          recovery:
            `Your last call to propose_edit was missing the editor-command anchor id. ` +
            `Re-call the tool with an explicit patch.anchor.id pulled from the entity outline, ` +
            `or ask the user a short clarifying question if the target block is genuinely ambiguous. ` +
            `Do not surface this validation error to the user as a chat message.`,
        },
      }
    }
    // Narrow loose tool-input shape (anchor.id?: string) to the strict wire
    // format (anchor.id: string) after defaulting from focus. Safe because
    // ctx.focus.id is a non-empty string here.
    const patch: AgentPatch = {
      ...inputPatch,
      anchor: { ...inputPatch.anchor, id: ctx.focus.id },
    }
    return { ok: true, summary: args.summary, patch }
  }
  // Either non-editor-command branch, or editor-command with an explicit
  // anchor.id — both are structurally compatible with the strict wire shape.
  return { ok: true, summary: args.summary, patch: inputPatch as AgentPatch }
}

export const createProposeEditTool = (
  deps: ProposeEditDeps,
  opts: { inline?: boolean } = {}
): Tool<z.infer<typeof proposeEditInputSchema>, ProposedEditSummary | InlineEditResult> => ({
  name: 'propose_edit',
  description:
    'Propose an edit to an entity. The edit is not applied until the user accepts it. Prefer this over direct mutation.',
  schema: proposeEditInputSchema,
  handler: async (args, ctx) => {
    const resolved = await resolvePatch(args, ctx, deps, opts)
    if (!resolved.ok) {
      return resolved
    }
    const { summary, patch } = resolved
    if (opts.inline) {
      // The inline in-document flow only ever applies document edits — reject
      // entity-metadata patches here rather than surface a bogus stale-anchor
      // error at apply time (apply-inline-edit only handles slate-ops /
      // editor-command).
      if (patch.kind === 'entity-metadata') {
        return { ok: false, error: 'entity-metadata edits are not supported in inline mode' }
      }
      return { ok: true, result: { kind: 'inline-edit', summary, patch } satisfies InlineEditResult }
    }
    const proposal = await deps.createProposal({
      agentId: ctx.agentId,
      entityId: args.entityId,
      userId: ctx.userId,
      summary,
      patch,
    })
    return { ok: true, result: proposal }
  },
})
